/**
 * Hook to fetch photos within a specific date range
 * Used by Report page for missed captures analysis
 */

import { useState, useEffect } from "react";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
} from "firebase/firestore";
import { db } from "@/config/firebase";
import type { Photo } from "@/types";
import { convertPhotoUrls } from "@/utils/storage";

interface UsePhotosInRangeParams {
  userId: string;
  viewId: string;
  rangeStart: Date;
  rangeEnd: Date;
  enabled?: boolean;
}

interface UsePhotosInRangeResult {
  photos: Photo[];
  loading: boolean;
  error: Error | null;
}

/**
 * Hook to fetch photos within a date range with real-time updates
 */
export function usePhotosInRange({
  userId,
  viewId,
  rangeStart,
  rangeEnd,
  enabled = true,
}: UsePhotosInRangeParams): UsePhotosInRangeResult {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!enabled || !userId || !viewId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const photosQuery = query(
      collection(db, "photos"),
      where("userId", "==", userId),
      where("viewId", "==", viewId),
      where("capturedAt", ">=", Timestamp.fromDate(rangeStart)),
      where("capturedAt", "<=", Timestamp.fromDate(rangeEnd)),
      orderBy("capturedAt", "desc")
    );

    const unsubscribe = onSnapshot(
      photosQuery,
      (snapshot) => {
        const photosList = snapshot.docs.map((doc) => {
          const data = { ...doc.data(), photoId: doc.id } as Photo;
          return convertPhotoUrls(data);
        });
        setPhotos(photosList);
        setLoading(false);
      },
      (err) => {
        console.error("Error fetching photos in range:", err);
        setError(err as Error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId, viewId, rangeStart.getTime(), rangeEnd.getTime(), enabled]);

  return {
    photos,
    loading,
    error,
  };
}
